import { CardContent, CardHeader, FormControlLabel, Paper, Popover, Switch, Table, TableBody, TableCell, TableRow } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Box } from '@mui/system';
import { isNil } from 'lodash';
import React, { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import atHomeIcon from '../../assets/images/at-home.svg';
import jobSiteIcon from '../../assets/images/job-site.svg';
import onRoadIcon from '../../assets/images/on-road.svg';
import { useIsRemediation } from '../../hooks/useIsRemediation';
import useSearchParams from '../../hooks/useSearchParams';
import Styled from '../../page/vehicleTracking/vehicleTracking.styled';
import { GroupSummaryType } from '../../redux/slices/dashboard/dashboardInterface';
import { groupSummary, selectGroupSummaryById } from '../../redux/slices/dashboard/dashboardSlice';
import { useDispatch } from '../../redux/store';
import { Card } from '../../styled/components';
import PickupStatusFilter, { parsePickupStatus } from '../filters/PickupStatusFilter';
import WeeksSinceDeliveryFilter from '../filters/WeeksSinceDeliveryFilter';
import { ControlType } from '../map/Common';
import { LeftBehind, ViewportBounds } from '../map/LeftBehind';
import { JOBSITE_ICON_DEFAULT, JOBSITE_ICON_RED, JOBSITE_ICON_YELLOW, PickupStatus } from '../map/jobSiteIcons';

type LeftBehindMapProps = {
  withTraffic?: boolean;
  withFullscreen?: boolean;
};

const MAP_MIN_HEIGHT = 300;

// Job-site pickup readiness, matching the pipeline board's split: not flagged =
// not ready; flagged with a pickup date (any) = scheduled; flagged with no date =
// ready (ready but unscheduled).
const pickupStatus = (readyForPickup?: boolean, pickupDate?: string): PickupStatus => {
  if (readyForPickup !== true) return 'not_ready';
  return pickupDate ? 'scheduled' : 'ready';
};

export const GroupSummaryMap: React.FC<LeftBehindMapProps> = ({ withTraffic, withFullscreen }: LeftBehindMapProps) => {
  const dispatch = useDispatch();
  const [params, setURLParams] = useSearchParams();
  const items: GroupSummaryType | undefined = useSelector(selectGroupSummaryById({ productId: params.productId }));
  const [mapHeight, setMapHeight] = useState(MAP_MIN_HEIGHT);
  const cardRef = useRef<HTMLDivElement>(null);
  const [fullScreen, setFullScreen] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const legendRef = useRef<HTMLDivElement>(null);
  const summaryRef = useRef<HTMLDivElement>(null);
  const isRemediation = useIsRemediation();
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'), { noSsr: true });
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [visibleBounds, setVisibleBounds] = useState<ViewportBounds | null>(null);

  const open = Boolean(anchorEl);

  const handleBoundsChange = useCallback((bounds: ViewportBounds) => {
    setVisibleBounds(bounds);
  }, []);

  const handleFullScreenChange = useCallback((fullScreen: boolean) => {
    setFullScreen(fullScreen);
  }, []);

  useEffect(() => {
    const setHeight = () => {
      const box = cardRef.current?.getBoundingClientRect();
      if (box) {
        setMapHeight(Math.max(MAP_MIN_HEIGHT, window.innerHeight - box.top - 32));
      }
    };
    setHeight();
    window.addEventListener('resize', setHeight);

    return () => {
      window.removeEventListener('resize', setHeight, false);
    };
  }, []);

  const markers = useMemo(() => {
    const minWeeks = params.weeksSinceDelivery ? Number(params.weeksSinceDelivery) : 0;
    const weekMs = 7 * 24 * 60 * 60 * 1000;
    const statusFilter = parsePickupStatus(params.pickupStatus);
    return (
      items?.result
        ?.filter((site) => site.longitude !== 0 && site.latitude !== 0)
        .filter((site) => {
          if (site.rec_type !== 'Job') return true;
          if (!minWeeks) return true;
          if (!site.last_delivered_at) return false;
          const weeksSince = Math.floor((Date.now() - new Date(site.last_delivered_at).getTime()) / weekMs);
          return weeksSince >= minWeeks;
        })
        // Pickup-status filter applies to job sites only; other types always show.
        .filter((site) => site.rec_type !== 'Job' || statusFilter.length === 0 || statusFilter.includes(pickupStatus(site.ready_for_pickup, site.pickup_date)))
        .map((site) => ({
          key: site.target_id ?? '',
          lat: site.latitude ?? 0,
          lng: site.longitude ?? 0,
          siteName: site.target_name,
          siteId: site.target_id,
          type: site.rec_type ? `${site.rec_type}_group` : '',
          toolsLeftBehind: !isNil(site.item_cnt) ? Number(site.item_cnt) : 0,
          hideCaption: params.displayLabels !== 'true',
          recType: site.rec_type,
          warehouseId: site.warehouse_id,
          maxDaysOnsite: site.max_days_onsite,
          status: site.rec_type === 'Job' ? pickupStatus(site.ready_for_pickup, site.pickup_date) : undefined,
          startedAt: site.started_at,
          pickupDate: site.pickup_date,
          notes: site.notes,
          blowerCnt: site.blower_cnt,
          dehumidifierCnt: site.dehumidifier_cnt,
          airScrubberCnt: site.air_scrubber_cnt,
          otherCnt: site.other_cnt,
        })) ?? []
    );
  }, [items, params.displayLabels, params.weeksSinceDelivery, params.pickupStatus]);

  useEffect(() => {
    dispatch(groupSummary({ productId: params.productId }));
  }, [dispatch, params.productId]);

  // Job-site markers within the current map viewport — recomputed as the user pans/zooms.
  const visibleJobMarkers = useMemo(() => {
    const inBounds = (lat: number, lng: number) => {
      if (!visibleBounds) return true;
      const { north, south, east, west } = visibleBounds;
      if (lat < south || lat > north) return false;
      return west <= east ? lng >= west && lng <= east : lng >= west || lng <= east;
    };
    return markers.filter((m) => m.recType === 'Job' && inBounds(m.lat, m.lng));
  }, [markers, visibleBounds]);

  // Bucket the in-view job sites by pickup status for the summary table.
  const bucketCounts = useMemo(() => {
    const counts = { red: 0, yellow: 0, green: 0 };
    visibleJobMarkers.forEach((m) => {
      if (m.status === 'ready') counts.yellow += 1;
      else if (m.status === 'scheduled') counts.green += 1;
      else counts.red += 1;
    });
    return counts;
  }, [visibleJobMarkers]);

  const controls = useMemo(
    () =>
      [
        {
          position: 'LEFT_TOP',
          ref: formRef,
        },
        {
          position: 'RIGHT_BOTTOM',
          ref: legendRef,
        },
        ...(isRemediation
          ? [
              {
                position: 'RIGHT_BOTTOM',
                ref: summaryRef,
              },
            ]
          : []),
      ] as ControlType[],
    [isRemediation],
  );

  const handleClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleInitMap = (iteration = 0) => {
    setTimeout(() => {
      if (!!formRef.current?.style.position) {
        setAnchorEl(buttonRef.current);
      } else if (iteration < 20) {
        handleInitMap(iteration + 1);
      }
    }, 50);
  };

  return (
    <Card sx={{ height: mapHeight }} ref={cardRef}>
      <LeftBehind
        zoom={11}
        markers={markers}
        withTraffic={withTraffic}
        withFullscreen={withFullscreen}
        onFullScreen={handleFullScreenChange}
        controls={controls}
        withLogo
        onMapInited={handleInitMap}
        iconSize="small"
        onBoundsChange={handleBoundsChange}
        fitRecType="Job"
      />
      <Box hidden>
        <Box ref={formRef} sx={{ ml: 2, mt: fullScreen ? 2 : 5 }}>
          <Styled.FormButton ref={buttonRef} variant="outlined" onClick={handleClick}>
            <Box component="div">Filters & Options</Box>
            {!open && params.pickupStatus && (
              <Box component="div">
                <PickupStatusFilter viewMode />
              </Box>
            )}
          </Styled.FormButton>
          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            disablePortal
            sx={{
              '& .MuiPaper-root': {
                boxShadow: (theme) => `${theme.shadows[1]} !important`,
                padding: '8px 16px',
              },
            }}
          >
            <Box component="div" sx={{ my: 4 }}>
              <PickupStatusFilter />
            </Box>
            <Box component="div" sx={{ my: 4 }}>
              <WeeksSinceDeliveryFilter />
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={params.displayLabels === 'true'}
                  onChange={(event) => setURLParams({ replace: false, newParams: { displayLabels: event.target.checked } })}
                  size={isMdUp ? 'medium' : 'small'}
                />
              }
              label="Display labels"
              sx={{ display: 'block' }}
            />
          </Popover>
        </Box>
      </Box>
      <Box hidden>
        <Box ref={legendRef} sx={{ mr: 2 }}>
          <Card elevation={1}>
            <CardHeader title="Legend" />
            <CardContent sx={{ pt: 0 }}>
              <FormControlLabel
                control={<Box component="img" src={jobSiteIcon} sx={{ width: 22, mr: 1 }} />}
                label="Job Site"
                sx={{ cursor: 'default', alignItems: 'center', display: 'flex', mb: 1 }}
              />
              <FormControlLabel
                control={<Box component="img" src={atHomeIcon} sx={{ width: 22, mr: 1 }} />}
                label="Company Site"
                sx={{ cursor: 'default', alignItems: 'center', display: 'flex', mb: 1 }}
              />
              <FormControlLabel
                control={<Box component="img" src={onRoadIcon} sx={{ width: 22, mr: 1 }} />}
                label="Vehicle"
                sx={{ cursor: 'default', alignItems: 'center', display: 'flex' }}
              />
            </CardContent>
          </Card>
        </Box>
      </Box>
      {isRemediation && (
        <Box hidden>
          <Box ref={summaryRef} sx={{ mr: 2, mb: 2 }}>
            <Paper elevation={1} variant="outlined" sx={{ border: '1px solid', borderColor: 'common.black' }}>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ py: 0.5, pr: 1, width: 28 }}>
                      <Box component="img" src={JOBSITE_ICON_RED} sx={{ width: 18, display: 'block' }} />
                    </TableCell>
                    <TableCell sx={{ py: 0.5 }}>Not ready</TableCell>
                    <TableCell sx={{ py: 0.5, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{bucketCounts.red}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ py: 0.5, pr: 1 }}>
                      <Box component="img" src={JOBSITE_ICON_YELLOW} sx={{ width: 18, display: 'block' }} />
                    </TableCell>
                    <TableCell sx={{ py: 0.5 }}>Ready</TableCell>
                    <TableCell sx={{ py: 0.5, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{bucketCounts.yellow}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ py: 0.5, pr: 1 }}>
                      <Box component="img" src={JOBSITE_ICON_DEFAULT} sx={{ width: 18, display: 'block' }} />
                    </TableCell>
                    <TableCell sx={{ py: 0.5 }}>Scheduled</TableCell>
                    <TableCell sx={{ py: 0.5, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{bucketCounts.green}</TableCell>
                  </TableRow>
                  <TableRow sx={{ '& td': { borderTop: '1px solid', borderTopColor: 'divider', fontWeight: 600 } }}>
                    <TableCell sx={{ py: 0.5 }} />
                    <TableCell sx={{ py: 0.5 }}>Total</TableCell>
                    <TableCell sx={{ py: 0.5, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {bucketCounts.green + bucketCounts.yellow + bucketCounts.red}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>
          </Box>
        </Box>
      )}
    </Card>
  );
};
